import { useState, useEffect } from "react";
import { toast } from "sonner";
import PageHeader from "./PageHeader";
import { Save, Plus, Trash2, Upload, Music, Key, Check, X, Mail, Radio, Image as ImageIcon } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "https://effortless-metaphor-api.ogtommype.workers.dev";

const INPUT = "w-full bg-[#111111] border border-[#27272A] p-2 text-white font-mono text-sm outline-none focus:border-neon";
const INPUT_HALF = "bg-[#111111] border border-[#27272A] p-2 text-white font-mono text-sm outline-none focus:border-neon";

const TABS = [
  { key: "label", label: "LABEL" },
  { key: "artist", label: "ARTIST" },
  { key: "latestRelease", label: "LATEST" },
  { key: "releases", label: "RELEASES" },
  { key: "beats", label: "BEATS" },
  { key: "playlists", label: "PLAYLISTS" },
  { key: "apps", label: "APPS" },
  { key: "storefront", label: "STOREFRONT" },
  { key: "platform", label: "PLATFORM" },
  { key: "products", label: "PRODUCTS" },
  { key: "orders", label: "ORDERS" },
  { key: "payments", label: "PAYMENTS" },
  { key: "store", label: "STORE" },
  { key: "transmissions", label: "TRANSMISSIONS" },
  { key: "messages", label: "MESSAGES" },
];

const API_PATH = { releases: "releases", beats: "beats", playlists: "playlists", apps: "apps", products: "products" };

export default function Admin() {
  const [apiKey, setApiKey] = useState("");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("label");
  const [uploading, setUploading] = useState(false);
  const [pendingTx, setPendingTx] = useState([]);
  const [messages, setMessages] = useState([]);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    const stored = sessionStorage.getItem("em_api_key");
    if (stored) {
      setApiKey(stored);
      checkAuth(stored);
    }
  }, []);

  const headers = (key) => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${key || apiKey}`,
  });

  const checkAuth = async (key) => {
    try {
      const res = await fetch(`${API_URL}/api/auth/check`, {
        headers: { Authorization: `Bearer ${key}` },
      });
      if (res.ok) {
        sessionStorage.setItem("em_api_key", key);
        setApiKey(key);
        setAuthed(true);
        const site = await fetch(`${API_URL}/api/site`);
        if (site.ok) setData(await site.json());
      } else {
        sessionStorage.removeItem("em_api_key");
        toast.error("Invalid API key");
      }
    } catch {
      toast.error("Failed to connect to API");
    }
  };

  const handleAuth = (e) => {
    e.preventDefault();
    checkAuth(apiKey);
  };

  const saveConfig = async (key) => {
    const res = await fetch(`${API_URL}/api/config/${key}`, {
      method: "PUT",
      headers: headers(),
      body: JSON.stringify({ value: data[key] }),
    });
    if (res.ok) toast.success(`${key} saved`);
    else toast.error(`Failed to save ${key}`);
  };

  const saveItem = async (section, item) => {
    const path = API_PATH[section];
    const res = await fetch(`${API_URL}/api/${path}/${item.id}`, {
      method: "PUT",
      headers: headers(),
      body: JSON.stringify(item),
    });
    if (res.ok) toast.success(`${item.id} saved`);
    else toast.error(`Failed to save ${item.id}`);
  };

  const addItem = async (section, template) => {
    const path = API_PATH[section];
    const res = await fetch(`${API_URL}/api/${path}`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify(template),
    });
    if (res.ok) {
      setData((prev) => ({ ...prev, [section]: [...prev[section], template] }));
      toast.success("Item added");
    } else {
      toast.error("Failed to add item");
    }
  };

  const deleteItem = async (section, id, index) => {
    const path = API_PATH[section];
    const res = await fetch(`${API_URL}/api/${path}/${id}`, {
      method: "DELETE",
      headers: headers(),
    });
    if (res.ok) {
      setData((prev) => ({
        ...prev,
        [section]: prev[section].filter((_, i) => i !== index),
      }));
      toast.success("Deleted");
    } else {
      toast.error("Failed to delete");
    }
  };

  const updateConfig = (section, field, value) => {
    setData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const updateItem = (section, index, field, value) => {
    setData((prev) => ({
      ...prev,
      [section]: prev[section].map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const updateConfigArray = (section, arrayKey, index, field, value) => {
    setData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayKey]: prev[section][arrayKey].map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        ),
      },
    }));
  };

  const addConfigArrayItem = (section, arrayKey, template) => {
    setData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayKey]: [...prev[section][arrayKey], template],
      },
    }));
  };

  const removeConfigArrayItem = (section, arrayKey, index) => {
    setData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayKey]: prev[section][arrayKey].filter((_, i) => i !== index),
      },
    }));
  };

  const handleAudioUpload = async (file, refType, refId, purpose, onSuccess) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("ref_type", refType);
      formData.append("ref_id", refId);
      formData.append("purpose", purpose);
      const res = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${apiKey}` },
        body: formData,
      });
      if (res.ok) {
        const result = await res.json();
        onSuccess(result);
        toast.success("Audio uploaded");
      } else {
        toast.error("Upload failed");
      }
    } catch {
      toast.error("Upload error");
    } finally {
      setUploading(false);
    }
  };

  const updateStreaming = (section, index, platform, url) => {
    setData((prev) => ({
      ...prev,
      [section]: prev[section].map((item, i) =>
        i === index
          ? { ...item, streaming: { ...item.streaming, [platform]: url } }
          : item
      ),
    }));
  };

  const removeStreaming = (section, index, platform) => {
    setData((prev) => ({
      ...prev,
      [section]: prev[section].map((item, i) => {
        if (i !== index) return item;
        const streaming = { ...item.streaming };
        delete streaming[platform];
        return { ...item, streaming };
      }),
    }));
  };

  const updatePurchaseLink = (index, platform, url) => {
    setData((prev) => ({
      ...prev,
      beats: prev.beats.map((item, i) =>
        i === index
          ? { ...item, purchaseLinks: { ...item.purchaseLinks, [platform]: url } }
          : item
      ),
    }));
  };

  const removePurchaseLink = (index, platform) => {
    setData((prev) => ({
      ...prev,
      beats: prev.beats.map((item, i) => {
        if (i !== index) return item;
        const purchaseLinks = { ...item.purchaseLinks };
        delete purchaseLinks[platform];
        return { ...item, purchaseLinks };
      }),
    }));
  };

  // ── Platform cards (site_config "platform") ───────────────────
  const addPlatformCard = () =>
    setData((prev) => ({
      ...prev,
      platform: [...(prev.platform || []), { id: `plat-${Date.now()}`, name: "", tagline: "", url: "", blurb: "", status: "live", featured: false, stats: [] }],
    }));

  const updatePlatform = (index, field, value) =>
    setData((prev) => ({ ...prev, platform: prev.platform.map((c, i) => (i === index ? { ...c, [field]: value } : c)) }));

  const removePlatformCard = (index) =>
    setData((prev) => ({ ...prev, platform: prev.platform.filter((_, i) => i !== index) }));

  const movePlatformCard = (index, dir) =>
    setData((prev) => {
      const arr = [...(prev.platform || [])];
      const j = index + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[index], arr[j]] = [arr[j], arr[index]];
      return { ...prev, platform: arr };
    });

  const addPlatformStat = (cardIndex) =>
    setData((prev) => ({
      ...prev,
      platform: prev.platform.map((c, i) => (i === cardIndex ? { ...c, stats: [...(c.stats || []), { value: "", label: "" }] } : c)),
    }));

  const updatePlatformStat = (cardIndex, statIndex, field, value) =>
    setData((prev) => ({
      ...prev,
      platform: prev.platform.map((c, i) =>
        i === cardIndex ? { ...c, stats: (c.stats || []).map((s, j) => (j === statIndex ? { ...s, [field]: value } : s)) } : c
      ),
    }));

  const removePlatformStat = (cardIndex, statIndex) =>
    setData((prev) => ({
      ...prev,
      platform: prev.platform.map((c, i) => (i === cardIndex ? { ...c, stats: (c.stats || []).filter((_, j) => j !== statIndex) } : c)),
    }));

  // ── Store config (site_config "store", nested merch/beats) ────
  const updateStore = (path, value) =>
    setData((prev) => {
      const store = { ...(prev.store || {}) };
      const parts = path.split(".");
      if (parts.length === 1) store[parts[0]] = value;
      else store[parts[0]] = { ...(store[parts[0]] || {}), [parts[1]]: value };
      return { ...prev, store };
    });

  // ── Products ──────────────────────────────────────────────────
  const addProduct = async () => {
    const t = { id: `prod-${Date.now()}`, name: "", category: "Apparel", description: "", price: 0, sizes: [], images: [], available: 1, sort_order: data.products?.length || 0 };
    const res = await fetch(`${API_URL}/api/products`, { method: "POST", headers: headers(), body: JSON.stringify(t) });
    if (res.ok) {
      setData((prev) => ({ ...prev, products: [...(prev.products || []), { ...t, available: true, imageKeys: [], imageUrls: [], imageUrl: null }] }));
      toast.success("Product added");
    } else toast.error("Failed to add product");
  };

  const saveProduct = async (p) => {
    const keys = p.imageKeys || (p.image_key ? [p.image_key] : []);
    const res = await fetch(`${API_URL}/api/products/${p.id}`, {
      method: "PUT", headers: headers(),
      body: JSON.stringify({
        name: p.name, category: p.category, description: p.description,
        price: Number(p.price) || 0, sizes: p.sizes || [],
        images: keys, image_key: keys[0] || null, available: p.available ? 1 : 0, sort_order: p.sort_order ?? 0,
      }),
    });
    if (res.ok) toast.success(`${p.name || p.id} saved`); else toast.error("Failed to save product");
  };

  const addProductImage = (index, result) =>
    setData((prev) => ({
      ...prev,
      products: prev.products.map((p, i) => i === index
        ? { ...p, imageKeys: [...(p.imageKeys || []), result.key], imageUrls: [...(p.imageUrls || []), result.audioUrl], image_key: (p.imageKeys && p.imageKeys[0]) || result.key, imageUrl: (p.imageUrls && p.imageUrls[0]) || result.audioUrl }
        : p),
    }));

  const removeProductImage = (index, idx) =>
    setData((prev) => ({
      ...prev,
      products: prev.products.map((p, i) => i === index
        ? { ...p, imageKeys: (p.imageKeys || []).filter((_, j) => j !== idx), imageUrls: (p.imageUrls || []).filter((_, j) => j !== idx) }
        : p),
    }));

  const deleteProduct = async (id, index) => {
    const res = await fetch(`${API_URL}/api/products/${id}`, { method: "DELETE", headers: headers() });
    if (res.ok) {
      setData((prev) => ({ ...prev, products: prev.products.filter((_, i) => i !== index) }));
      toast.success("Deleted");
    } else toast.error("Failed to delete");
  };

  // ── Payments config (site_config "payments"; NO secret keys here) ──
  const updatePayments = (path, value) =>
    setData((prev) => {
      const pay = { ...(prev.payments || {}) };
      const parts = path.split(".");
      if (parts.length === 1) pay[parts[0]] = value;
      else pay[parts[0]] = { ...(pay[parts[0]] || {}), [parts[1]]: value };
      return { ...prev, payments: pay };
    });

  // ── Orders ────────────────────────────────────────────────────
  const loadOrders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/orders`, { headers: { Authorization: `Bearer ${apiKey}` } });
      if (res.ok) setOrders(await res.json());
    } catch { /* ignore */ }
  };

  const setOrderStatus = async (id, status) => {
    const res = await fetch(`${API_URL}/api/orders/${id}`, { method: "PUT", headers: headers(), body: JSON.stringify({ status }) });
    if (res.ok) { setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o))); toast.success(status); }
  };

  const deleteOrder = async (id) => {
    const res = await fetch(`${API_URL}/api/orders/${id}`, { method: "DELETE", headers: headers() });
    if (res.ok) { setOrders((prev) => prev.filter((o) => o.id !== id)); toast.success("Deleted"); }
  };

  // ── Tracks (per release) ──────────────────────────────────────
  const trackKeyFromUrl = (url) => (url ? url.split("/api/audio/")[1] : null);

  const addTrack = async (releaseId, releaseIndex) => {
    const tracks = data.releases[releaseIndex].tracks || [];
    const track = { id: `trk-${Date.now()}`, title: "", trackNum: tracks.length + 1, duration: "", streaming: {}, audio_key: null, audioUrl: null };
    const res = await fetch(`${API_URL}/api/releases/${releaseId}/tracks`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ id: track.id, title: track.title, track_num: track.trackNum, duration: track.duration, streaming: track.streaming, audio_key: track.audio_key }),
    });
    if (res.ok) {
      setData((prev) => ({
        ...prev,
        releases: prev.releases.map((r, i) => (i === releaseIndex ? { ...r, tracks: [...(r.tracks || []), track] } : r)),
      }));
      toast.success("Track added");
    } else {
      toast.error("Failed to add track");
    }
  };

  const updateTrack = (releaseIndex, trackIndex, field, value) => {
    setData((prev) => ({
      ...prev,
      releases: prev.releases.map((r, i) =>
        i === releaseIndex
          ? { ...r, tracks: r.tracks.map((t, j) => (j === trackIndex ? { ...t, [field]: value } : t)) }
          : r
      ),
    }));
  };

  const saveTrack = async (track) => {
    const res = await fetch(`${API_URL}/api/tracks/${track.id}`, {
      method: "PUT",
      headers: headers(),
      body: JSON.stringify({
        title: track.title,
        track_num: track.trackNum,
        duration: track.duration,
        streaming: track.streaming || {},
        audio_key: track.audio_key || trackKeyFromUrl(track.audioUrl),
      }),
    });
    if (res.ok) toast.success("Track saved");
    else toast.error("Failed to save track");
  };

  const deleteTrack = async (trackId, releaseIndex, trackIndex) => {
    const res = await fetch(`${API_URL}/api/tracks/${trackId}`, { method: "DELETE", headers: headers() });
    if (res.ok) {
      setData((prev) => ({
        ...prev,
        releases: prev.releases.map((r, i) => (i === releaseIndex ? { ...r, tracks: r.tracks.filter((_, j) => j !== trackIndex) } : r)),
      }));
      toast.success("Track deleted");
    } else {
      toast.error("Failed to delete track");
    }
  };

  const loadPendingTransmissions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/transmissions?status=pending`, {
        headers: { Authorization: `Bearer ${apiKey}` },
      });
      if (res.ok) setPendingTx(await res.json());
    } catch { /* ignore */ }
  };

  const moderateTransmission = async (id, status) => {
    const res = await fetch(`${API_URL}/api/transmissions/${id}`, {
      method: "PUT",
      headers: headers(),
      body: JSON.stringify({ status }),
    });
    if (res.ok) {
      setPendingTx((prev) => prev.filter((t) => t.id !== id));
      toast.success(status === "approved" ? "Approved" : "Rejected");
    }
  };

  const deleteTransmission = async (id) => {
    const res = await fetch(`${API_URL}/api/transmissions/${id}`, {
      method: "DELETE",
      headers: headers(),
    });
    if (res.ok) {
      setPendingTx((prev) => prev.filter((t) => t.id !== id));
      setData((prev) => ({
        ...prev,
        transmissions: (prev.transmissions || []).filter((t) => t.id !== id),
      }));
      toast.success("Deleted");
    }
  };

  const postTransmission = async (form) => {
    const res = await fetch(`${API_URL}/api/transmissions`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify(form),
    });
    if (res.ok) {
      toast.success("Posted");
      const site = await fetch(`${API_URL}/api/site`);
      if (site.ok) {
        const updated = await site.json();
        setData((prev) => ({ ...prev, transmissions: updated.transmissions }));
      }
    }
  };

  const loadMessages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messages`, {
        headers: { Authorization: `Bearer ${apiKey}` },
      });
      if (res.ok) setMessages(await res.json());
    } catch { /* ignore */ }
  };

  const deleteMessage = async (id) => {
    const res = await fetch(`${API_URL}/api/messages/${id}`, {
      method: "DELETE",
      headers: headers(),
    });
    if (res.ok) {
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Deleted");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="em-card p-8 max-w-md w-full">
          <div className="flex items-center gap-2 mb-6">
            <Key size={20} className="text-neon" />
            <h2 className="font-display font-black text-2xl">Admin Access</h2>
          </div>
          <form onSubmit={handleAuth}>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter API key"
              className={`${INPUT} mb-4`}
            />
            <button
              type="submit"
              className="w-full bg-neon text-black font-bold py-3 font-mono text-sm uppercase tracking-wider hover:bg-[#00CC55] transition-colors"
            >
              Authenticate
            </button>
          </form>
          <p className="text-neutral-600 text-xs mt-4 font-mono">
            // API key set via: wrangler secret put API_KEY
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <p className="label-mono text-neutral-500">// loading content...</p>
      </div>
    );
  }

  const StreamingEditor = ({ section, index, streaming }) => {
    const [customName, setCustomName] = useState("");
    return (
      <div className="space-y-2">
        <label className="label-mono text-neutral-500 block">STREAMING</label>
        {Object.entries(streaming || {}).map(([platform, url]) => (
          <div key={platform} className="flex gap-2">
            <span className="bg-[#111111] border border-[#27272A] p-2 text-neutral-400 font-mono text-sm min-w-[120px]">
              {platform}
            </span>
            <input
              value={url}
              onChange={(e) => updateStreaming(section, index, platform, e.target.value)}
              className={`flex-1 ${INPUT_HALF}`}
            />
            <button
              onClick={() => removeStreaming(section, index, platform)}
              className="p-2 text-red-500 hover:text-red-400"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <div className="flex gap-2">
          <select
            id={`add-platform-${section}-${index}`}
            className={INPUT_HALF}
            defaultValue=""
          >
            <option value="" disabled>Add platform...</option>
            {["spotify", "soundcloud", "apple_music", "tidal", "youtube_music", "bandcamp"]
              .filter((p) => !streaming?.[p])
              .map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            <option value="__custom__">Custom...</option>
          </select>
          <input
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="custom name"
            className={`${INPUT_HALF} w-32`}
            style={{ display: document.getElementById(`add-platform-${section}-${index}`)?.value === "__custom__" ? "block" : "none" }}
          />
          <button
            onClick={() => {
              const sel = document.getElementById(`add-platform-${section}-${index}`);
              let name = sel.value;
              if (name === "__custom__") {
                name = customName.trim().toLowerCase().replace(/\s+/g, "_");
                if (!name) return;
                setCustomName("");
              }
              if (name && name !== "__custom__") {
                updateStreaming(section, index, name, "");
                sel.value = "";
              }
            }}
            className="label-mono text-neon text-sm flex items-center gap-1"
          >
            <Plus size={14} /> Add
          </button>
        </div>
      </div>
    );
  };

  const PurchaseLinksEditor = ({ index, links }) => {
    const [customName, setCustomName] = useState("");
    return (
      <div className="space-y-2">
        <label className="label-mono text-neutral-500 block">PURCHASE LINKS</label>
        {Object.entries(links || {}).map(([platform, url]) => (
          <div key={platform} className="flex gap-2">
            <span className="bg-[#111111] border border-[#27272A] p-2 text-neutral-400 font-mono text-sm min-w-[120px]">
              {platform}
            </span>
            <input
              value={url}
              onChange={(e) => updatePurchaseLink(index, platform, e.target.value)}
              className={`flex-1 ${INPUT_HALF}`}
              placeholder="https://..."
            />
            <button
              onClick={() => removePurchaseLink(index, platform)}
              className="p-2 text-red-500 hover:text-red-400"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <div className="flex gap-2">
          <input
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="Platform name (e.g. beatstars)"
            className={INPUT_HALF}
          />
          <button
            onClick={() => {
              const name = customName.trim().toLowerCase().replace(/\s+/g, "_");
              if (name) {
                updatePurchaseLink(index, name, "");
                setCustomName("");
              }
            }}
            className="label-mono text-neon text-sm flex items-center gap-1"
          >
            <Plus size={14} /> Add
          </button>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="page-admin">
      <PageHeader
        code="99"
        label="ADMIN"
        title="Admin Panel"
        subtitle="Manage content. Changes save directly to the database — no deploy needed."
      />

      <section>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
          <div className="flex gap-2 mb-6 flex-wrap">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                  if (tab.key === "transmissions") loadPendingTransmissions();
                  if (tab.key === "messages") loadMessages();
                  if (tab.key === "orders") loadOrders();
                }}
                className={`label-mono px-4 py-2 border transition-colors ${
                  activeTab === tab.key
                    ? "border-neon text-neon"
                    : "border-[#27272A] text-neutral-400 hover:text-neon"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="em-card p-6">
            {activeTab === "label" && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-display font-bold text-xl">Label Info</h3>
                  <button onClick={() => saveConfig("label")} className="label-mono text-neon text-sm flex items-center gap-2 border border-neon/40 px-3 py-1 hover:bg-neon/10">
                    <Save size={14} /> Save
                  </button>
                </div>
                {["name", "tagline", "shortDescription", "established", "city", "contactEmail"].map((key) => (
                  <div key={key}>
                    <label className="label-mono text-neutral-500 block mb-2">{key.toUpperCase()}</label>
                    {key === "shortDescription" ? (
                      <textarea value={data.label[key] || ""} onChange={(e) => updateConfig("label", key, e.target.value)} rows={3} className={INPUT} />
                    ) : (
                      <input value={data.label[key] || ""} onChange={(e) => updateConfig("label", key, e.target.value)} className={INPUT} />
                    )}
                  </div>
                ))}
                <div>
                  <label className="label-mono text-neutral-500 block mb-2">SOCIALS</label>
                  {(data.label.socials || []).map((s, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <input value={s.label} onChange={(e) => updateConfigArray("label", "socials", i, "label", e.target.value)} placeholder="Label" className={`flex-1 ${INPUT_HALF}`} />
                      <input value={s.url} onChange={(e) => updateConfigArray("label", "socials", i, "url", e.target.value)} placeholder="URL" className={`flex-1 ${INPUT_HALF}`} />
                      <button onClick={() => removeConfigArrayItem("label", "socials", i)} className="p-2 text-red-500 hover:text-red-400"><Trash2 size={16} /></button>
                    </div>
                  ))}
                  <button onClick={() => addConfigArrayItem("label", "socials", { label: "", url: "" })} className="label-mono text-neon text-sm flex items-center gap-2">
                    <Plus size={14} /> Add Social
                  </button>
                </div>
              </div>
            )}

            {activeTab === "artist" && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-display font-bold text-xl">Artist Info</h3>
                  <button onClick={() => saveConfig("artist")} className="label-mono text-neon text-sm flex items-center gap-2 border border-neon/40 px-3 py-1 hover:bg-neon/10">
                    <Save size={14} /> Save
                  </button>
                </div>
                <div>
                  <label className="label-mono text-neutral-500 block mb-2">NAME</label>
                  <input value={data.artist.name || ""} onChange={(e) => updateConfig("artist", "name", e.target.value)} className={INPUT} />
                </div>
                <div>
                  <label className="label-mono text-neutral-500 block mb-2">ROLE</label>
                  <input value={data.artist.role || ""} onChange={(e) => updateConfig("artist", "role", e.target.value)} className={INPUT} />
                </div>
                <div>
                  <label className="label-mono text-neutral-500 block mb-2">BIO</label>
                  <textarea value={data.artist.bio || ""} onChange={(e) => updateConfig("artist", "bio", e.target.value)} rows={4} className={INPUT} />
                </div>
                <div>
                  <label className="label-mono text-neutral-500 block mb-2">STATS</label>
                  {(data.artist.stats || []).map((s, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <input value={s.label} onChange={(e) => updateConfigArray("artist", "stats", i, "label", e.target.value)} placeholder="Label" className={`flex-1 ${INPUT_HALF}`} />
                      <input value={s.value} onChange={(e) => updateConfigArray("artist", "stats", i, "value", e.target.value)} placeholder="Value" className={`flex-1 ${INPUT_HALF}`} />
                      <button onClick={() => removeConfigArrayItem("artist", "stats", i)} className="p-2 text-red-500 hover:text-red-400"><Trash2 size={16} /></button>
                    </div>
                  ))}
                  <button onClick={() => addConfigArrayItem("artist", "stats", { label: "", value: "" })} className="label-mono text-neon text-sm flex items-center gap-2">
                    <Plus size={14} /> Add Stat
                  </button>
                </div>
              </div>
            )}

            {activeTab === "latestRelease" && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-display font-bold text-xl">Latest Release</h3>
                  <button onClick={() => saveConfig("latestRelease")} className="label-mono text-neon text-sm flex items-center gap-2 border border-neon/40 px-3 py-1 hover:bg-neon/10">
                    <Save size={14} /> Save
                  </button>
                </div>
                {["title", "type", "date", "runtime"].map((key) => (
                  <div key={key}>
                    <label className="label-mono text-neutral-500 block mb-2">{key.toUpperCase()}</label>
                    <input value={data.latestRelease[key] || ""} onChange={(e) => updateConfig("latestRelease", key, e.target.value)} className={INPUT} />
                  </div>
                ))}
                <div>
                  <label className="label-mono text-neutral-500 block mb-2">DESCRIPTION</label>
                  <textarea value={data.latestRelease.description || ""} onChange={(e) => updateConfig("latestRelease", "description", e.target.value)} rows={3} placeholder="What this drop is about..." className={INPUT} />
                </div>
                <div className="border border-[#27272A] p-3">
                  <label className="label-mono text-neutral-500 block mb-2 flex items-center gap-2">
                    <Music size={14} /> Audio Preview (from vault)
                  </label>
                  {data.latestRelease.audioUrl || data.latestRelease.audio_key ? (
                    <div className="space-y-2">
                      <audio controls src={data.latestRelease.audioUrl} className="w-full h-8" />
                      <div className="text-xs font-mono text-neutral-500">key: {data.latestRelease.audio_key}</div>
                    </div>
                  ) : (
                    <label className="em-card p-3 flex items-center justify-center gap-2 cursor-pointer hover:border-neon transition-colors">
                      <Upload size={16} />
                      <span className="text-sm">{uploading ? "Uploading..." : "Upload Audio"}</span>
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleAudioUpload(e.target.files[0], "latest", "release", "preview", (result) => {
                              updateConfig("latestRelease", "audio_key", result.key);
                              updateConfig("latestRelease", "audioUrl", result.audioUrl);
                            });
                          }
                        }}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                  )}
                </div>
                <div className="border border-[#27272A] p-3">
                  <label className="label-mono text-neutral-500 block mb-2 flex items-center gap-2">
                    <ImageIcon size={14} /> Cover Art
                  </label>
                  {data.latestRelease.coverUrl ? (
                    <div className="flex items-center gap-3">
                      <img src={data.latestRelease.coverUrl} alt="" className="w-16 h-16 object-cover border border-[#27272A]" />
                      <button
                        onClick={() => { updateConfig("latestRelease", "cover", ""); updateConfig("latestRelease", "coverUrl", ""); }}
                        className="label-mono text-red-500 text-xs hover:text-red-400"
                      >
                        Remove (then Save)
                      </button>
                    </div>
                  ) : (
                    <label className="em-card p-3 flex items-center justify-center gap-2 cursor-pointer hover:border-neon transition-colors">
                      <Upload size={16} />
                      <span className="text-sm">{uploading ? "Uploading..." : "Upload Cover (then Save)"}</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleAudioUpload(e.target.files[0], "latest", "release", "cover", (result) => {
                              updateConfig("latestRelease", "cover", result.key);
                              updateConfig("latestRelease", "coverUrl", result.audioUrl);
                            });
                          }
                        }}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                  )}
                </div>
              </div>
            )}

            {activeTab === "releases" && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-display font-bold text-xl">Releases</h3>
                  <button
                    onClick={() => addItem("releases", { id: `rel-${Date.now()}`, title: "", type: "Single", year: new Date().getFullYear(), runtime: "0:00", track_count: 0, streaming: {}, sort_order: data.releases.length })}
                    className="label-mono text-neon text-sm flex items-center gap-2"
                  >
                    <Plus size={14} /> Add Release
                  </button>
                </div>
                {data.releases.map((r, i) => (
                  <div key={r.id} className="border border-[#27272A] p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="label-mono text-neutral-500">{r.id}</span>
                      <div className="flex gap-2">
                        <button onClick={() => saveItem("releases", { ...r, track_count: r.trackCount })} className="label-mono text-neon text-sm flex items-center gap-1"><Save size={14} /> Save</button>
                        <button onClick={() => deleteItem("releases", r.id, i)} className="text-red-500 hover:text-red-400"><Trash2 size={16} /></button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={r.title} onChange={(e) => updateItem("releases", i, "title", e.target.value)} placeholder="Title" className={INPUT_HALF} />
                      <select value={r.type} onChange={(e) => updateItem("releases", i, "type", e.target.value)} className={INPUT_HALF}>
                        <option>Album</option><option>EP</option><option>Single</option><option>Mixtape</option>
                      </select>
                      <input type="number" value={r.year} onChange={(e) => updateItem("releases", i, "year", parseInt(e.target.value) || 0)} placeholder="Year" className={INPUT_HALF} />
                      <input value={r.runtime} onChange={(e) => updateItem("releases", i, "runtime", e.target.value)} placeholder="Runtime" className={INPUT_HALF} />
                      <input type="number" value={r.trackCount ?? r.track_count ?? 0} onChange={(e) => updateItem("releases", i, "trackCount", parseInt(e.target.value) || 0)} placeholder="Track Count" className={INPUT_HALF} />
                    </div>
                    <StreamingEditor section="releases" index={i} streaming={r.streaming} />

                    {/* Cover art */}
                    <div className="border border-[#27272A] p-3">
                      <label className="label-mono text-neutral-500 block mb-2 flex items-center gap-2">
                        <ImageIcon size={14} /> Cover Art
                      </label>
                      {r.coverUrl ? (
                        <div className="flex items-center gap-3">
                          <img src={r.coverUrl} alt="" className="w-16 h-16 object-cover border border-[#27272A]" />
                          <button
                            onClick={() => { updateItem("releases", i, "cover", null); updateItem("releases", i, "coverUrl", null); }}
                            className="label-mono text-red-500 text-xs hover:text-red-400"
                          >
                            Remove (then Save)
                          </button>
                        </div>
                      ) : (
                        <label className="em-card p-3 flex items-center justify-center gap-2 cursor-pointer hover:border-neon transition-colors">
                          <Upload size={16} />
                          <span className="text-sm">{uploading ? "Uploading..." : "Upload Cover (then Save)"}</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                handleAudioUpload(e.target.files[0], "release", r.id, "cover", (result) => {
                                  updateItem("releases", i, "cover", result.key);
                                  updateItem("releases", i, "coverUrl", result.audioUrl);
                                });
                              }
                            }}
                            className="hidden"
                            disabled={uploading}
                          />
                        </label>
                      )}
                    </div>

                    {/* Tracks */}
                    <div className="border border-[#27272A] p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="label-mono text-neutral-500 flex items-center gap-2"><Music size={14} /> Tracks</label>
                        <button onClick={() => addTrack(r.id, i)} className="label-mono text-neon text-xs flex items-center gap-1"><Plus size={12} /> Add Track</button>
                      </div>
                      {(r.tracks || []).map((t, ti) => (
                        <div key={t.id} className="border border-[#1A1A1A] p-2 space-y-2">
                          <div className="flex items-center gap-2">
                            <input type="number" value={t.trackNum ?? ti + 1} onChange={(e) => updateTrack(i, ti, "trackNum", parseInt(e.target.value) || 0)} className={`${INPUT_HALF} w-14`} placeholder="#" />
                            <input value={t.title} onChange={(e) => updateTrack(i, ti, "title", e.target.value)} className={`${INPUT_HALF} flex-1`} placeholder="Title" />
                            <input value={t.duration || ""} onChange={(e) => updateTrack(i, ti, "duration", e.target.value)} className={`${INPUT_HALF} w-20`} placeholder="3:24" />
                            <button onClick={() => saveTrack(t)} className="label-mono text-neon text-xs flex items-center px-2 py-2 border border-neon/40 hover:bg-neon/10" title="Save track"><Save size={12} /></button>
                            <button onClick={() => deleteTrack(t.id, i, ti)} className="text-red-500 hover:text-red-400" title="Delete track"><Trash2 size={14} /></button>
                          </div>
                          {t.audioUrl ? (
                            <audio controls src={t.audioUrl} className="w-full h-8" />
                          ) : (
                            <label className="em-card p-2 flex items-center justify-center gap-2 cursor-pointer hover:border-neon transition-colors text-xs">
                              <Upload size={14} /> {uploading ? "Uploading..." : "Upload track audio (then Save)"}
                              <input
                                type="file"
                                accept="audio/*"
                                onChange={(e) => {
                                  if (e.target.files[0]) {
                                    handleAudioUpload(e.target.files[0], "track", t.id, "audio", (result) => {
                                      updateTrack(i, ti, "audio_key", result.key);
                                      updateTrack(i, ti, "audioUrl", result.audioUrl);
                                    });
                                  }
                                }}
                                className="hidden"
                                disabled={uploading}
                              />
                            </label>
                          )}
                        </div>
                      ))}
                      {(r.tracks || []).length === 0 && <p className="label-mono text-neutral-600 text-xs">// no tracks yet</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "beats" && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-display font-bold text-xl">Beats</h3>
                  <button
                    onClick={() => addItem("beats", { id: `BX-${String(data.beats.length + 1).padStart(3, "0")}`, name: "", bpm: 140, key: "C min", mood: "", tags: [], price: 49 })}
                    className="label-mono text-neon text-sm flex items-center gap-2"
                  >
                    <Plus size={14} /> Add Beat
                  </button>
                </div>
                {data.beats.map((beat, i) => (
                  <div key={beat.id} className="border border-[#27272A] p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="label-mono text-neutral-500">{beat.id}</span>
                      <div className="flex gap-2">
                        <button onClick={() => saveItem("beats", { ...beat, audio_key: beat.audio_key || beat.audioKey, purchase_links: beat.purchaseLinks })} className="label-mono text-neon text-sm flex items-center gap-1"><Save size={14} /> Save</button>
                        <button onClick={() => deleteItem("beats", beat.id, i)} className="text-red-500 hover:text-red-400"><Trash2 size={16} /></button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={beat.name} onChange={(e) => updateItem("beats", i, "name", e.target.value)} placeholder="Name" className={INPUT_HALF} />
                      <input type="number" value={beat.bpm} onChange={(e) => updateItem("beats", i, "bpm", parseInt(e.target.value) || 0)} placeholder="BPM" className={INPUT_HALF} />
                      <input value={beat.key} onChange={(e) => updateItem("beats", i, "key", e.target.value)} placeholder="Key" className={INPUT_HALF} />
                      <input value={beat.mood} onChange={(e) => updateItem("beats", i, "mood", e.target.value)} placeholder="Mood" className={INPUT_HALF} />
                      <input type="number" value={beat.price} onChange={(e) => updateItem("beats", i, "price", parseInt(e.target.value) || 0)} placeholder="Price" className={INPUT_HALF} />
                    </div>
                    <input
                      value={Array.isArray(beat.tags) ? beat.tags.join(", ") : ""}
                      onChange={(e) => updateItem("beats", i, "tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
                      placeholder="Tags (comma separated)"
                      className={INPUT}
                    />
                    <div className="border border-[#27272A] p-3">
                      <label className="label-mono text-neutral-500 block mb-2 flex items-center gap-2">
                        <Music size={14} /> Audio File
                      </label>
                      {beat.audioUrl ? (
                        <audio controls src={beat.audioUrl} className="w-full h-8" />
                      ) : (
                        <label className="em-card p-3 flex items-center justify-center gap-2 cursor-pointer hover:border-neon transition-colors">
                          <Upload size={16} />
                          <span className="text-sm">{uploading ? "Uploading..." : "Upload Audio"}</span>
                          <input
                            type="file"
                            accept="audio/*"
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                handleAudioUpload(e.target.files[0], "beat", beat.id, "preview", (result) => {
                                  updateItem("beats", i, "audio_key", result.key);
                                  updateItem("beats", i, "audioUrl", result.audioUrl);
                                });
                              }
                            }}
                            className="hidden"
                            disabled={uploading}
                          />
                        </label>
                      )}
                    </div>
                    <PurchaseLinksEditor index={i} links={beat.purchaseLinks} />
                  </div>
                ))}
              </div>
            )}

            {activeTab === "playlists" && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-display font-bold text-xl">Playlists</h3>
                  <button
                    onClick={() => addItem("playlists", { id: `pl-${Date.now()}`, title: "", platform: "spotify", embed_url: "", sort_order: data.playlists.length })}
                    className="label-mono text-neon text-sm flex items-center gap-2"
                  >
                    <Plus size={14} /> Add Playlist
                  </button>
                </div>
                {data.playlists.map((p, i) => (
                  <div key={p.id} className="border border-[#27272A] p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="label-mono text-neutral-500">{p.id}</span>
                      <div className="flex gap-2">
                        <button onClick={() => saveItem("playlists", { ...p, embed_url: p.embedUrl || p.embed_url })} className="label-mono text-neon text-sm flex items-center gap-1"><Save size={14} /> Save</button>
                        <button onClick={() => deleteItem("playlists", p.id, i)} className="text-red-500 hover:text-red-400"><Trash2 size={16} /></button>
                      </div>
                    </div>
                    <input value={p.title} onChange={(e) => updateItem("playlists", i, "title", e.target.value)} placeholder="Title" className={INPUT} />
                    <input value={p.embedUrl || p.embed_url || ""} onChange={(e) => updateItem("playlists", i, "embedUrl", e.target.value)} placeholder="Embed URL" className={INPUT} />
                    <select value={p.platform} onChange={(e) => updateItem("playlists", i, "platform", e.target.value)} className={INPUT}>
                      <option value="spotify">Spotify</option>
                      <option value="soundcloud">SoundCloud</option>
                    </select>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "apps" && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-display font-bold text-xl">Apps</h3>
                  <button
                    onClick={() => addItem("apps", { id: `app-${Date.now()}`, name: "", version: "v1.0.0", category: "", description: "", status: "coming soon", url: "#", sort_order: data.apps.length })}
                    className="label-mono text-neon text-sm flex items-center gap-2"
                  >
                    <Plus size={14} /> Add App
                  </button>
                </div>
                {data.apps.map((app, i) => (
                  <div key={app.id} className="border border-[#27272A] p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="label-mono text-neutral-500">{app.id}</span>
                      <div className="flex gap-2">
                        <button onClick={() => saveItem("apps", app)} className="label-mono text-neon text-sm flex items-center gap-1"><Save size={14} /> Save</button>
                        <button onClick={() => deleteItem("apps", app.id, i)} className="text-red-500 hover:text-red-400"><Trash2 size={16} /></button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={app.name} onChange={(e) => updateItem("apps", i, "name", e.target.value)} placeholder="Name" className={INPUT_HALF} />
                      <input value={app.version} onChange={(e) => updateItem("apps", i, "version", e.target.value)} placeholder="Version" className={INPUT_HALF} />
                      <input value={app.category} onChange={(e) => updateItem("apps", i, "category", e.target.value)} placeholder="Category" className={INPUT_HALF} />
                      <select value={app.status} onChange={(e) => updateItem("apps", i, "status", e.target.value)} className={INPUT_HALF}>
                        <option value="live">Live</option>
                        <option value="beta">Beta</option>
                        <option value="alpha">Alpha</option>
                        <option value="internal">Internal</option>
                        <option value="coming soon">Coming Soon</option>
                      </select>
                    </div>
                    <textarea value={app.description} onChange={(e) => updateItem("apps", i, "description", e.target.value)} placeholder="Description" rows={2} className={INPUT} />
                    <input value={app.url} onChange={(e) => updateItem("apps", i, "url", e.target.value)} placeholder="URL" className={INPUT} />
                  </div>
                ))}
              </div>
            )}

            {activeTab === "storefront" && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-display font-bold text-xl">Storefront</h3>
                  <button onClick={() => saveConfig("storefront")} className="label-mono text-neon text-sm flex items-center gap-2 border border-neon/40 px-3 py-1 hover:bg-neon/10">
                    <Save size={14} /> Save
                  </button>
                </div>
                <p className="text-neutral-400 text-sm">Universal storefront banner shown at the top of the Beatbox page. Links to your primary beat marketplace.</p>
                <div>
                  <label className="label-mono text-neutral-500 block mb-2">TITLE</label>
                  <input value={data.storefront?.title || ""} onChange={(e) => updateConfig("storefront", "title", e.target.value)} placeholder="Browse the full catalog" className={INPUT} />
                </div>
                <div>
                  <label className="label-mono text-neutral-500 block mb-2">URL</label>
                  <input value={data.storefront?.url || ""} onChange={(e) => updateConfig("storefront", "url", e.target.value)} placeholder="https://beatstars.com/..." className={INPUT} />
                </div>
                <div>
                  <label className="label-mono text-neutral-500 block mb-2">DESCRIPTION</label>
                  <input value={data.storefront?.description || ""} onChange={(e) => updateConfig("storefront", "description", e.target.value)} placeholder="Find and license beats" className={INPUT} />
                </div>
              </div>
            )}

            {activeTab === "platform" && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-display font-bold text-xl">The Platform</h3>
                  <div className="flex gap-2">
                    <button onClick={addPlatformCard} className="label-mono text-neon text-sm flex items-center gap-1"><Plus size={14} /> Add Card</button>
                    <button onClick={() => saveConfig("platform")} className="label-mono text-neon text-sm flex items-center gap-2 border border-neon/40 px-3 py-1 hover:bg-neon/10"><Save size={14} /> Save</button>
                  </div>
                </div>
                <p className="text-neutral-400 text-sm">Cards in the homepage "Built different" section. Mark one as <span className="text-neon">Featured</span> for the large left card (with stats). Order top-to-bottom = display order. Remember to Save.</p>
                {(data.platform || []).map((c, i) => (
                  <div key={c.id || i} className="border border-[#27272A] p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="label-mono text-neutral-500">{c.featured ? "★ FEATURED" : `#${i + 1}`}</span>
                      <div className="flex gap-2 items-center">
                        <button onClick={() => movePlatformCard(i, -1)} disabled={i === 0} className="label-mono text-neutral-400 hover:text-neon disabled:opacity-30 px-2" title="Move up">↑</button>
                        <button onClick={() => movePlatformCard(i, 1)} disabled={i === (data.platform.length - 1)} className="label-mono text-neutral-400 hover:text-neon disabled:opacity-30 px-2" title="Move down">↓</button>
                        <button onClick={() => removePlatformCard(i)} className="text-red-500 hover:text-red-400" title="Delete card"><Trash2 size={16} /></button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={c.name || ""} onChange={(e) => updatePlatform(i, "name", e.target.value)} placeholder="Name (e.g. TRAPWORLD)" className={INPUT_HALF} />
                      <input value={c.tagline || ""} onChange={(e) => updatePlatform(i, "tagline", e.target.value)} placeholder="Tagline / domain" className={INPUT_HALF} />
                      <input value={c.url || ""} onChange={(e) => updatePlatform(i, "url", e.target.value)} placeholder="https://..." className={INPUT_HALF} />
                      <input value={c.status || ""} onChange={(e) => updatePlatform(i, "status", e.target.value)} placeholder="status: live / in dev / coming soon" className={INPUT_HALF} />
                    </div>
                    <textarea value={c.blurb || ""} onChange={(e) => updatePlatform(i, "blurb", e.target.value)} rows={2} placeholder="Description" className={INPUT} />
                    <label className="label-mono text-neutral-400 flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!c.featured} onChange={(e) => updatePlatform(i, "featured", e.target.checked)} />
                      Featured (large card with stats)
                    </label>
                    {c.featured && (
                      <div className="border border-[#1A1A1A] p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="label-mono text-neutral-500">STATS</label>
                          <button onClick={() => addPlatformStat(i)} className="label-mono text-neon text-xs flex items-center gap-1"><Plus size={12} /> Add Stat</button>
                        </div>
                        {(c.stats || []).map((s, si) => (
                          <div key={si} className="flex gap-2">
                            <input value={s.value || ""} onChange={(e) => updatePlatformStat(i, si, "value", e.target.value)} placeholder="Value (e.g. FREE)" className={`flex-1 ${INPUT_HALF}`} />
                            <input value={s.label || ""} onChange={(e) => updatePlatformStat(i, si, "label", e.target.value)} placeholder="Label (e.g. for artists)" className={`flex-1 ${INPUT_HALF}`} />
                            <button onClick={() => removePlatformStat(i, si)} className="p-2 text-red-500 hover:text-red-400"><Trash2 size={14} /></button>
                          </div>
                        ))}
                        {(c.stats || []).length === 0 && <p className="label-mono text-neutral-600 text-xs">// no stats</p>}
                      </div>
                    )}
                  </div>
                ))}
                {(data.platform || []).length === 0 && <p className="label-mono text-neutral-600 text-xs">// no cards — add one</p>}
              </div>
            )}

            {activeTab === "products" && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-display font-bold text-xl">Products</h3>
                  <button onClick={addProduct} className="label-mono text-neon text-sm flex items-center gap-2"><Plus size={14} /> Add Product</button>
                </div>
                {(data.products || []).map((p, i) => (
                  <div key={p.id} className="border border-[#27272A] p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="label-mono text-neutral-500">{p.id}</span>
                      <div className="flex gap-2">
                        <button onClick={() => saveProduct(p)} className="label-mono text-neon text-sm flex items-center gap-1"><Save size={14} /> Save</button>
                        <button onClick={() => deleteProduct(p.id, i)} className="text-red-500 hover:text-red-400"><Trash2 size={16} /></button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={p.name} onChange={(e) => updateItem("products", i, "name", e.target.value)} placeholder="Name" className={INPUT_HALF} />
                      <input value={p.category || ""} onChange={(e) => updateItem("products", i, "category", e.target.value)} placeholder="Category (Apparel, Accessories...)" className={INPUT_HALF} />
                      <input type="number" step="0.01" value={p.price} onChange={(e) => updateItem("products", i, "price", parseFloat(e.target.value) || 0)} placeholder="Price" className={INPUT_HALF} />
                      <input value={Array.isArray(p.sizes) ? p.sizes.join(", ") : ""} onChange={(e) => updateItem("products", i, "sizes", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="Sizes (S, M, L) — blank for none" className={INPUT_HALF} />
                    </div>
                    <textarea value={p.description || ""} onChange={(e) => updateItem("products", i, "description", e.target.value)} rows={2} placeholder="Description" className={INPUT} />
                    <label className="label-mono text-neutral-400 flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={p.available !== false} onChange={(e) => updateItem("products", i, "available", e.target.checked)} /> Available (uncheck = sold out)
                    </label>
                    <div className="border border-[#27272A] p-3">
                      <label className="label-mono text-neutral-500 block mb-2 flex items-center gap-2"><ImageIcon size={14} /> Product Images</label>
                      <div className="flex flex-wrap gap-2">
                        {(p.imageUrls || []).map((u, idx) => (
                          <div key={idx} className="relative">
                            <img src={u} alt="" className="w-16 h-20 object-cover border border-[#27272A]" />
                            {idx === 0 && <span className="absolute bottom-0 left-0 label-mono text-[9px] bg-neon text-black px-1">MAIN</span>}
                            <button onClick={() => removeProductImage(i, idx)} className="absolute -top-2 -right-2 bg-[#0A0A0A] border border-red-500 text-red-500 w-5 h-5 leading-none text-xs hover:bg-red-500 hover:text-white">×</button>
                          </div>
                        ))}
                        <label className="w-16 h-20 border border-dashed border-[#27272A] flex items-center justify-center cursor-pointer hover:border-neon">
                          <Upload size={16} className="text-neutral-500" />
                          <input type="file" accept="image/*" onChange={(e) => { if (e.target.files[0]) handleAudioUpload(e.target.files[0], "product", p.id, "photo", (result) => addProductImage(i, result)); }} className="hidden" disabled={uploading} />
                        </label>
                      </div>
                      <p className="label-mono text-neutral-600 text-xs mt-2">// first = main image · upload adds · × removes · Save after changes</p>
                    </div>
                  </div>
                ))}
                {(data.products || []).length === 0 && <p className="label-mono text-neutral-600 text-xs">// no products — add one</p>}
              </div>
            )}

            {activeTab === "orders" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-bold text-xl">Orders</h3>
                  <button onClick={loadOrders} className="label-mono text-neutral-500 text-xs hover:text-neon">Refresh</button>
                </div>
                {orders.length === 0 && <p className="text-neutral-500 text-sm font-mono">// no orders</p>}
                {orders.map((o) => {
                  let items = []; try { items = JSON.parse(o.items); } catch { /* */ }
                  let ship = {}; try { ship = o.shipping ? JSON.parse(o.shipping) : {}; } catch { /* */ }
                  return (
                    <div key={o.id} className="border border-[#27272A] p-4 space-y-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="label-mono text-neon">{o.id} · <span className="text-neutral-500">{o.created_at?.split("T")[0]}</span></div>
                        <div className="flex items-center gap-2">
                          <span className={`label-mono px-2 py-0.5 border ${o.status === "paid" || o.status === "fulfilled" ? "text-neon border-neon/40" : "text-yellow-400 border-yellow-500/40"}`}>{o.status}</span>
                          <select value={o.status} onChange={(e) => setOrderStatus(o.id, e.target.value)} className="bg-[#111111] border border-[#27272A] text-xs font-mono p-1">
                            <option value="pending">pending</option><option value="paid">paid</option><option value="fulfilled">fulfilled</option><option value="cancelled">cancelled</option>
                          </select>
                          <button onClick={() => deleteOrder(o.id)} className="text-red-500 hover:text-red-400"><Trash2 size={14} /></button>
                        </div>
                      </div>
                      <div className="text-sm text-neutral-300">{o.customer_name} · {o.customer_email}</div>
                      {ship.address && <div className="text-xs text-neutral-500 font-mono">{ship.address}, {ship.city} {ship.state} {ship.zip}</div>}
                      <div className="text-xs font-mono text-neutral-400">
                        {items.map((it, k) => (<div key={k}>• {it.qty}× {it.name}{it.size ? ` (${it.size})` : ""} — ${Number(it.price).toFixed(2)}</div>))}
                      </div>
                      <div className="font-mono text-neon">Total ${Number(o.total).toFixed(2)} · via {o.payment_method || "—"}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {activeTab === "payments" && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-display font-bold text-xl">Payment Methods</h3>
                  <button onClick={() => saveConfig("payments")} className="label-mono text-neon text-sm flex items-center gap-2 border border-neon/40 px-3 py-1 hover:bg-neon/10"><Save size={14} /> Save</button>
                </div>
                <p className="text-neutral-400 text-sm">Enable what you accept; these appear at checkout. <span className="text-neon">Your Stripe secret key is never stored here</span> — it lives as a server secret (tell me your keys and I'll wire it).</p>
                <div className="border border-[#27272A] p-4 space-y-2">
                  <label className="label-mono text-neutral-300 flex items-center gap-2"><input type="checkbox" checked={!!data.payments?.stripe?.enabled} onChange={(e) => updatePayments("stripe.enabled", e.target.checked)} /> Stripe (cards)</label>
                  <input value={data.payments?.stripe?.publishableKey || ""} onChange={(e) => updatePayments("stripe.publishableKey", e.target.value)} placeholder="Stripe publishable key (pk_live_...) — optional" className={INPUT} />
                  <p className="label-mono text-neutral-600 text-xs">// real card checkout also needs the secret key set server-side.</p>
                </div>
                <div className="border border-[#27272A] p-4 space-y-2">
                  <label className="label-mono text-neutral-300 flex items-center gap-2"><input type="checkbox" checked={!!data.payments?.paypal?.enabled} onChange={(e) => updatePayments("paypal.enabled", e.target.checked)} /> PayPal</label>
                  <input value={data.payments?.paypal?.handle || ""} onChange={(e) => updatePayments("paypal.handle", e.target.value)} placeholder="PayPal.me handle (e.g. ogtommyp)" className={INPUT} />
                </div>
                <div className="border border-[#27272A] p-4 space-y-2">
                  <label className="label-mono text-neutral-300 flex items-center gap-2"><input type="checkbox" checked={!!data.payments?.cashapp?.enabled} onChange={(e) => updatePayments("cashapp.enabled", e.target.checked)} /> Cash App</label>
                  <input value={data.payments?.cashapp?.cashtag || ""} onChange={(e) => updatePayments("cashapp.cashtag", e.target.value)} placeholder="$cashtag" className={INPUT} />
                </div>
                <div className="border border-[#27272A] p-4 space-y-2">
                  <label className="label-mono text-neutral-300 flex items-center gap-2"><input type="checkbox" checked={!!data.payments?.venmo?.enabled} onChange={(e) => updatePayments("venmo.enabled", e.target.checked)} /> Venmo</label>
                  <input value={data.payments?.venmo?.handle || ""} onChange={(e) => updatePayments("venmo.handle", e.target.value)} placeholder="Venmo handle (without @)" className={INPUT} />
                </div>
                <div className="border border-[#27272A] p-4 space-y-2">
                  <label className="label-mono text-neutral-300 flex items-center gap-2"><input type="checkbox" checked={!!data.payments?.zelle?.enabled} onChange={(e) => updatePayments("zelle.enabled", e.target.checked)} /> Zelle</label>
                  <input value={data.payments?.zelle?.handle || ""} onChange={(e) => updatePayments("zelle.handle", e.target.value)} placeholder="Zelle email or phone" className={INPUT} />
                </div>
              </div>
            )}

            {activeTab === "store" && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-display font-bold text-xl">Store Page</h3>
                  <button onClick={() => saveConfig("store")} className="label-mono text-neon text-sm flex items-center gap-2 border border-neon/40 px-3 py-1 hover:bg-neon/10"><Save size={14} /> Save</button>
                </div>
                <p className="text-neutral-400 text-sm">Headline + the beats link on the <span className="text-neon">/store</span> page. Manage merch items in the PRODUCTS tab.</p>
                <div>
                  <label className="label-mono text-neutral-500 block mb-2">HEADLINE</label>
                  <input value={data.store?.headline || ""} onChange={(e) => updateStore("headline", e.target.value)} placeholder="Rep the label." className={INPUT} />
                </div>
                <div className="border border-[#27272A] p-4 space-y-3">
                  <div className="label-mono text-neon">// BEATS (SoundClick)</div>
                  <div className="grid grid-cols-2 gap-2">
                    <input value={data.store?.beats?.title || ""} onChange={(e) => updateStore("beats.title", e.target.value)} placeholder="Title (License the beats)" className={INPUT_HALF} />
                    <input value={data.store?.beats?.url || ""} onChange={(e) => updateStore("beats.url", e.target.value)} placeholder="https://www.soundclick.com/OGTommyP/" className={INPUT_HALF} />
                  </div>
                  <textarea value={data.store?.beats?.description || ""} onChange={(e) => updateStore("beats.description", e.target.value)} rows={2} placeholder="Short description" className={INPUT} />
                </div>
              </div>
            )}

            {activeTab === "transmissions" && (
              <TransmissionsTab
                data={data}
                pendingTx={pendingTx}
                moderateTransmission={moderateTransmission}
                deleteTransmission={deleteTransmission}
                postTransmission={postTransmission}
                loadPendingTransmissions={loadPendingTransmissions}
              />
            )}

            {activeTab === "messages" && (
              <MessagesTab messages={messages} deleteMessage={deleteMessage} loadMessages={loadMessages} />
            )}
          </div>

          <p className="text-neutral-500 text-xs mt-6 label-mono">
            // changes save directly to the database — no deploy needed
          </p>
        </div>
      </section>
    </div>
  );
}

function TransmissionsTab({ data, pendingTx, moderateTransmission, deleteTransmission, postTransmission, loadPendingTransmissions }) {
  const [newTx, setNewTx] = useState({ handle: "", title: "", message: "", type: "origin" });

  return (
    <div className="space-y-6">
      <h3 className="font-display font-bold text-xl">Transmissions</h3>

      {/* Post new */}
      <div className="border border-[#27272A] p-4 space-y-3">
        <div className="label-mono text-neon">// POST NEW TRANSMISSION</div>
        <div className="grid grid-cols-2 gap-2">
          <input value={newTx.handle} onChange={(e) => setNewTx({ ...newTx, handle: e.target.value })} placeholder="Handle (e.g. EM_ORIGIN)" className="bg-[#111111] border border-[#27272A] p-2 text-white font-mono text-sm outline-none focus:border-neon" />
          <select value={newTx.type} onChange={(e) => setNewTx({ ...newTx, type: e.target.value })} className="bg-[#111111] border border-[#27272A] p-2 text-white font-mono text-sm outline-none focus:border-neon">
            <option value="origin">Origin</option>
            <option value="community">Community</option>
          </select>
        </div>
        <input value={newTx.title} onChange={(e) => setNewTx({ ...newTx, title: e.target.value })} placeholder="Title (optional)" className="w-full bg-[#111111] border border-[#27272A] p-2 text-white font-mono text-sm outline-none focus:border-neon" />
        <textarea value={newTx.message} onChange={(e) => setNewTx({ ...newTx, message: e.target.value })} placeholder="Message..." rows={3} className="w-full bg-[#111111] border border-[#27272A] p-2 text-white font-mono text-sm outline-none focus:border-neon resize-none" />
        <button
          onClick={() => {
            if (!newTx.message.trim()) return;
            postTransmission(newTx);
            setNewTx({ handle: "", title: "", message: "", type: "origin" });
          }}
          className="label-mono text-neon text-sm flex items-center gap-2 border border-neon/40 px-3 py-1 hover:bg-neon/10"
        >
          <Radio size={14} /> Broadcast
        </button>
      </div>

      {/* Pending queue */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="label-mono text-yellow-500">// PENDING REVIEW — {pendingTx.length}</div>
          <button onClick={loadPendingTransmissions} className="label-mono text-neutral-500 text-xs hover:text-neon">Refresh</button>
        </div>
        {pendingTx.length === 0 && <p className="text-neutral-500 text-sm font-mono">No pending transmissions.</p>}
        {pendingTx.map((t) => (
          <div key={t.id} className="border border-[#27272A] p-3 mb-2 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="label-mono text-neutral-500">{t.handle} · {t.created_at?.split("T")[0]}</div>
              <p className="text-neutral-300 text-sm mt-1 break-words">{t.message}</p>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <button onClick={() => moderateTransmission(t.id, "approved")} className="p-2 text-green-500 hover:text-green-400 border border-[#27272A] hover:border-green-500" title="Approve">
                <Check size={14} />
              </button>
              <button onClick={() => moderateTransmission(t.id, "rejected")} className="p-2 text-red-500 hover:text-red-400 border border-[#27272A] hover:border-red-500" title="Reject">
                <X size={14} />
              </button>
              <button onClick={() => deleteTransmission(t.id)} className="p-2 text-neutral-500 hover:text-red-400 border border-[#27272A]" title="Delete">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Approved transmissions */}
      <div>
        <div className="label-mono text-neutral-500 mb-3">// APPROVED — {(data.transmissions || []).length}</div>
        {(data.transmissions || []).map((t) => (
          <div key={t.id} className="border border-[#27272A] p-3 mb-2 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="label-mono text-neon">{t.handle}</span>
                {t.type === "origin" && <span className="label-mono text-xs bg-neon/10 text-neon px-1">ORIGIN</span>}
                <span className="label-mono text-neutral-600">{t.createdAt?.split("T")[0]}</span>
              </div>
              {t.title && <div className="font-display font-bold mt-1">{t.title}</div>}
              <p className="text-neutral-300 text-sm mt-1 break-words">{t.message}</p>
            </div>
            <button onClick={() => deleteTransmission(t.id)} className="p-2 text-neutral-500 hover:text-red-400 flex-shrink-0">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function MessagesTab({ messages, deleteMessage, loadMessages }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-xl flex items-center gap-2">
          <Mail size={20} /> Messages
        </h3>
        <button onClick={loadMessages} className="label-mono text-neutral-500 text-xs hover:text-neon">Refresh</button>
      </div>
      <p className="text-neutral-400 text-sm">Contact form submissions from the homepage.</p>
      {messages.length === 0 && <p className="text-neutral-500 text-sm font-mono">// no messages</p>}
      {messages.map((m) => (
        <div key={m.id} className={`border p-4 ${m.read_at ? "border-[#27272A]" : "border-neon/30"}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="label-mono text-neon">{m.handle}</span>
              <span className="label-mono text-neutral-600">{m.created_at?.split("T")[0]}</span>
              {!m.read_at && <span className="w-2 h-2 bg-neon rounded-full" />}
            </div>
            <button onClick={() => deleteMessage(m.id)} className="p-1 text-neutral-500 hover:text-red-400">
              <Trash2 size={14} />
            </button>
          </div>
          <p className="text-neutral-300 text-sm mt-2 whitespace-pre-wrap">{m.message}</p>
        </div>
      ))}
    </div>
  );
}
